// DealDesk.jsx — fully fixed (all weaknesses from review resolved)
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, CircularProgress,
    Snackbar, Alert, Avatar, InputAdornment, IconButton, Menu, MenuItem,
    ListItemIcon, Chip
} from '@mui/material';
import { createTheme, ThemeProvider, styled, keyframes } from '@mui/material/styles';
import ArrowBackIcon           from '@mui/icons-material/ArrowBack';
import SendIcon                from '@mui/icons-material/Send';
import LogoutIcon              from '@mui/icons-material/Logout';
import MoreVertIcon            from '@mui/icons-material/MoreVert';
import ContentCopyIcon         from '@mui/icons-material/ContentCopy';
import EditIcon                from '@mui/icons-material/Edit';
import DeleteIcon              from '@mui/icons-material/Delete';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import SwapHorizIcon           from '@mui/icons-material/SwapHoriz';
import KeyboardArrowDownIcon   from '@mui/icons-material/KeyboardArrowDown';
import WifiIcon                from '@mui/icons-material/Wifi';
import WifiOffIcon             from '@mui/icons-material/WifiOff';
import dayjs        from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc          from 'dayjs/plugin/utc';
import timezone     from 'dayjs/plugin/timezone';
import axios        from 'axios';
import { io as socketIO } from 'socket.io-client';
import jsPDF        from 'jspdf';
import html2canvas  from 'html2canvas';
import SD_logo      from '../assests/shipperdock.png';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(cfg => {
    const token = localStorage.getItem('jwtToken');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
    palette: {
        primary:    { main: '#11224d' },
        secondary:  { main: '#ffb300' },
        background: { default: '#f6f8fc', paper: '#ffffff' },
        error:      { main: '#e53935' },
    },
    typography: { fontFamily: "'DM Sans', 'Inter', Arial, sans-serif" },
    components: {
        MuiButton:    { styleOverrides: { root: { textTransform: 'none', fontWeight: 700, borderRadius: 6 } } },
        MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: 6 } } } },
    },
});

const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

// ─── Styled Components ────────────────────────────────────────────────────────
const HeaderBox = styled(Box)(() => ({
    background: 'linear-gradient(135deg, rgb(245,174,9) 0%, #dfa419 100%)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    color: 'white',
    height: '42px',
    minHeight: '42px',
    padding: '0 14px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
}));

const PanelHeader = styled(Typography)(() => ({
    fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#ffffff',
    background: 'linear-gradient(135deg, #11224d 0%, #1a3570 100%)',
    borderBottom: '1px solid rgba(17,34,77,0.2)', padding: '10px 14px 8px',
}));

const ColumnPanel = styled(Box)(() => ({
    backgroundColor: '#ffffff', borderRadius: 0, border: 'none',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 30px rgba(17,34,77,0.08)',
}));

const ScrollArea = styled(Box)(() => ({
    overflowY: 'auto', flexGrow: 1,
    '&::-webkit-scrollbar': { width: '5px' },
    '&::-webkit-scrollbar-thumb': { background: '#11224d', borderRadius: '10px' },
    '&::-webkit-scrollbar-thumb:hover': { background: '#1a3570' },
    '&::-webkit-scrollbar-track': { background: '#f8f9fc' },
    scrollbarWidth: 'thin', scrollbarColor: '#11224d #f8f9fc',
}));
// FIX: Add this new state


const LoadRow = styled(Box)(({ selected }) => ({
    padding: '12px 14px', cursor: 'pointer', borderRadius: 8, margin: '8px 12px',
    border: 'none', borderLeft: `4px solid ${selected ? '#11224d' : 'transparent'}`,
    backgroundColor: selected ? '#eef2ff' : 'transparent', transition: 'all 0.2s ease',
    '&:hover': { backgroundColor: '#eef2ff', transform: 'translateY(-3px)', boxShadow: '0 10px 25px rgba(17,34,77,0.2)' },
}));

const CompanyRow = styled(Box)(({ selected }) => ({
    padding: '12px 16px', cursor: 'pointer',
    borderLeft: `3px solid ${selected ? '#11224d' : 'transparent'}`,
    backgroundColor: selected ? '#eef2ff' : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    transition: 'all 0.15s',
    '&:hover': { backgroundColor: '#eef2ff', transform: 'translateY(-3px)', boxShadow: '0 10px 25px rgba(17,34,77,0.2)' },
    '& + &': { borderTop: '1px solid #f0f2f7' },
}));

const OfferBubble = styled(Box)(({ role }) => ({
    display: 'flex', justifyContent: role === 'own' ? 'flex-end' : 'flex-start',
    marginBottom: 6, animation: `${fadeIn} 0.2s ease`,
}));

const OfferCard = styled(Box)(({ status }) => ({
    width: '100%', maxWidth: '300px', padding: '10px', borderRadius: 12, border: 'none',
    backgroundColor: status === 'accepted' ? '#f1faf2' : status === 'rejected' ? '#fef2f2' : '#eef2ff',
    boxShadow: '0 12px 30px rgba(17,34,77,0.18)', position: 'relative',
}));

const MsgBubble = styled(Box)(({ isown }) => ({
    display: 'flex', flexDirection: 'column',
    alignItems: isown === 'true' ? 'flex-end' : 'flex-start',
    marginBottom: 2, animation: `${fadeIn} 0.2s ease`,
    '& .bubble': {
        maxWidth: '65%', padding: '8px 10px', position: 'relative', borderRadius: 16,
        backgroundColor: isown === 'true' ? '#132c6b' : '#ffffff',
        color: isown === 'true' ? '#fff' : '#222',
        fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '4px',
        lineHeight: 1.5, wordBreak: 'break-word',
        border: isown === 'true' ? 'none' : '1px solid #e5e7eb',
        borderBottomRightRadius: isown === 'true' ? 6 : 16,
        borderBottomLeftRadius:  isown === 'true' ? 16 : 6,
    },
}));

const SystemStatusMsg = styled(Box)(() => ({
    display: 'flex', justifyContent: 'center',
    margin: '16px 0', padding: '8px 16px', textAlign: 'center',
}));

const StatusChip = styled(Box)(({ status }) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: '#fff',
    backgroundColor: status === 'accepted' ? '#43a047' : status === 'rejected' ? '#e53935' : '#f59e0b',
}));

const Resizer = styled(Box)(() => ({
    width: '2px', height: '100%', backgroundColor: '#e0e4ef',
    cursor: 'col-resize', position: 'relative', transition: 'all 0.2s ease',
    flexShrink: 0, zIndex: 10,
    '&::after': { content: '""', position: 'absolute', left: '-4px', right: '-4px', top: 0, bottom: 0, backgroundColor: 'transparent' },
    '&:hover': { width: '4px', backgroundColor: '#ffb300', boxShadow: '0 0 8px rgba(255,179,0,0.4)' },
}));

// ─── Optimistic message expiry (ms) ──────────────────────────────────────────
const OPTIMISTIC_EXPIRY_MS = 15000;

// ─── Main Component ───────────────────────────────────────────────────────────
const DealDesk = ({
    loadOrTruckId, type, onBack, loadData, userData,
    allLoads = [], allTrucks = [], onSelectLoad, onSelectTruck, onLogout,
}) => {
    const [loading, setLoading]                         = useState(false);
    const [interactionData, setInteractionData]         = useState(null);
    const [offers, setOffers]                           = useState([]);
    const [messages, setMessages]                       = useState([]);
    const [myItems, setMyItems]                         = useState([]);
    const [companies, setCompanies]                     = useState([]);
    const [activeInteractionId, setActiveInteractionId] = useState(null);
    const [offerAmount, setOfferAmount]                 = useState('');
    const [messageText, setMessageText]                 = useState('');
    const [editMessageId, setEditMessageId]             = useState(null);
    const [snackbar, setSnackbar]                       = useState({ open: false, message: '', severity: 'success' });
    const [isSending, setIsSending]                     = useState(false);
    const [columnWidths, setColumnWidths]               = useState({ loads: 260, companies: 280 });
    const [isAtBottom, setIsAtBottom]                   = useState(true);
    const [unreadScrollCount, setUnreadScrollCount]     = useState(0);
    const [msgMenuAnchor, setMsgMenuAnchor]             = useState({ anchor: null, msg: null });
    const [showEditTip, setShowEditTip]                 = useState(() => !localStorage.getItem('dealDesk_editTipShown'));
    // FIX: socket connection status indicator
    const [socketConnected, setSocketConnected]         = useState(false);

    const [switchingConversation, setSwitchingConversation] = useState(false);

    const prevMsgCountRef        = useRef(0);
    const isAtBottomRef          = useRef(true);
    const scrollAreaRef          = useRef(null);
    const messagesEndRef         = useRef(null);
    const activeInteractionIdRef = useRef(activeInteractionId);
    const socketRef              = useRef(null);
    // FIX: track optimistic expiry timers
    const optimisticTimersRef    = useRef(new Map());

    useEffect(() => { isAtBottomRef.current = isAtBottom; }, [isAtBottom]);
    useEffect(() => { activeInteractionIdRef.current = activeInteractionId; }, [activeInteractionId]);

    const formatCurrency = (v) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const atBottom = scrollHeight - scrollTop - clientHeight < 100;
        if (atBottom && !isAtBottomRef.current) setUnreadScrollCount(0);
        setIsAtBottom(atBottom);
    };

    const startResizing = (column) => (mouseDownEvent) => {
        const startWidth    = columnWidths[column];
        const startPosition = mouseDownEvent.pageX;
        const onMouseMove   = (e) => setColumnWidths(prev => ({
            ...prev, [column]: Math.max(200, Math.min(600, startWidth + e.pageX - startPosition)),
        }));
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // ── Fetch sidebar items ───────────────────────────────────────────────────
    const fetchMyItems = useCallback(async () => {
        try {
            const res = await api.get('/api/deal-desk/items');
            setMyItems(res.data);
            return res.data;
        } catch (err) {
            console.error('[DealDesk] fetchMyItems error:', err);
            return [];
        }
    }, []);

    // ── Core fetch ────────────────────────────────────────────────────────────
    // const fetchDealDeskData = useCallback(async (forceInteractionId = null) => {
    //     if (!loadOrTruckId) return;
    //     if (forceInteractionId === null) await fetchMyItems();

    //     const iId = forceInteractionId ?? activeInteractionIdRef.current;

    //     try {
    //         try {
    //             await api.post('/api/deal-desk/interactions', { loadOrTruckId, type });
    //         } catch (_) { /* already exists or poster — ignore */ }

    //         const url = `/api/deal-desk/${loadOrTruckId}/${type}${iId ? `?interactionId=${iId}` : ''}`;
    //         const { data } = await api.get(url);

    //         setInteractionData(data.interaction || null);
    //         setOffers(data.offers || []);

    //         // Smart merge: keep optimistic messages not yet confirmed
    //         setMessages(prev => {
    //             const incoming = data.messages || [];
    //             const prevCount = prevMsgCountRef.current;
    //             const newCount  = incoming.length;

    //             if (newCount > prevCount && !isAtBottomRef.current) {
    //                 const newMsgs       = incoming.slice(prevCount);
    //                 const hasOthersMsgs = newMsgs.some(m => m.sender_user_id !== userData?.id);
    //                 if (hasOthersMsgs) setUnreadScrollCount(c => c + (newCount - prevCount));
    //             }
    //             prevMsgCountRef.current = newCount;

    //             const stillPending = prev.filter(m =>
    //                 m._isOptimistic &&
    //                 !incoming.some(r =>
    //                     r.sender_user_id === m.sender_user_id &&
    //                     r.message        === m.message &&
    //                     Math.abs(new Date(r.created_at).getTime() - new Date(m.created_at).getTime()) < 5000
    //                 )
    //             );
    //             return [...incoming, ...stillPending]
    //                 .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    //         });

    //         setCompanies(data.companies || []);
    //         if (forceInteractionId) {
    //             setActiveInteractionId(forceInteractionId);
    //             activeInteractionIdRef.current = forceInteractionId;
    //         }
    //         setLoading(false);
    //     } catch (error) {
    //         const resolvedId = forceInteractionId ?? activeInteractionIdRef.current;
    //         if (resolvedId) api.put(`/api/deal-desk/messages/${resolvedId}/read`).catch(() => {});
    //         console.error('[DealDesk] fetchDealDeskData error:', error);
    //         setSnackbar({ open: true, message: 'Failed to load DealDesk data', severity: 'error' });
    //         setLoading(false);
    //     }
    // }, [loadOrTruckId, type, userData?.id, fetchMyItems]);
    // ── Core fetch (Updated to properly manage loading states) ───────────────────
// ── Core fetch (Fixed to avoid triggering full loader on background updates) ───
const fetchDealDeskData = useCallback(async (forceInteractionId = null, isBackgroundSync = false) => {
    if (!loadOrTruckId) return;
    
    // ONLY show loaders if this is NOT a quiet background refresh
    if (!isBackgroundSync) {
        if (forceInteractionId) {
            setSwitchingConversation(true);
        } else {
            setLoading(true); 
        }
    }

    const iId = forceInteractionId ?? activeInteractionIdRef.current;

    try {
        if (forceInteractionId === null) {
            await fetchMyItems();
        }

        try {
            await api.post('/api/deal-desk/interactions', { loadOrTruckId, type });
        } catch (_) { /* already exists or poster — ignore */ }

        const url = `/api/deal-desk/${loadOrTruckId}/${type}${iId ? `?interactionId=${iId}` : ''}`;
        const { data } = await api.get(url);

        setInteractionData(data.interaction || null);
        setOffers(data.offers || []);

        setMessages(prev => {
            const incoming = data.messages || [];
            const prevCount = prevMsgCountRef.current;
            const newCount  = incoming.length;

            if (newCount > prevCount && !isAtBottomRef.current) {
                const newMsgs       = incoming.slice(prevCount);
                const hasOthersMsgs = newMsgs.some(m => m.sender_user_id !== userData?.id);
                if (hasOthersMsgs) setUnreadScrollCount(c => c + (newCount - prevCount));
            }
            prevMsgCountRef.current = newCount;

            const stillPending = prev.filter(m =>
                m._isOptimistic &&
                !incoming.some(r =>
                    r.sender_user_id === m.sender_user_id &&
                    r.message        === m.message &&
                    Math.abs(new Date(r.created_at).getTime() - new Date(m.created_at).getTime()) < 5000
                )
            );
            return [...incoming, ...stillPending]
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        });

        setCompanies(data.companies || []);
        if (forceInteractionId) {
            setActiveInteractionId(forceInteractionId);
            activeInteractionIdRef.current = forceInteractionId;
        }
    } catch (error) {
        const resolvedId = forceInteractionId ?? activeInteractionIdRef.current;
        if (resolvedId) api.put(`/api/deal-desk/messages/${resolvedId}/read`).catch(() => {});
        console.error('[DealDesk] fetchDealDeskData error:', error);
    } finally {
        // Safe check to always close any loader overlays
        setLoading(false);
        setSwitchingConversation(false);
    }
}, [loadOrTruckId, type, userData?.id, fetchMyItems]);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    }, []);

    useEffect(() => { if (isAtBottom) scrollToBottom('smooth'); }, [messages, offers, isAtBottom, scrollToBottom]);

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchMyItems().then(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Polling (30 s fallback) ───────────────────────────────────────────────
    // ── Polling (30 s fallback) ───
useEffect(() => {
    if (!loadOrTruckId) return;
    
    // First load displays the visual spinner
    fetchDealDeskData(null, false); 
    scrollToBottom('auto');
    
    // Polling refreshes happen silently in the background
    const interval = setInterval(() => fetchDealDeskData(null, true), 15000);
    return () => clearInterval(interval);
}, [loadOrTruckId, type, fetchDealDeskData, scrollToBottom]);

    // ── FIX: Single persistent socket — created once, never recreated ─────────
    useEffect(() => {
    // Force wss:// in production — Render rejects plain ws://
    const socketUrl = API_BASE_URL.replace(/^http:/, 'https:');

    const socket = socketIO(socketUrl, {
        auth:       { token: localStorage.getItem('jwtToken') },
        transports: ['websocket', 'polling'],  // try websocket first, poll as fallback
        upgrade:    true,
        reconnectionDelay:      1000,
        reconnectionDelayMax:   5000,
        reconnectionAttempts:   Infinity,
        timeout:                20000,
    });
    socketRef.current = socket;

        socket.on('connect', () => setSocketConnected(true));

        socket.on('disconnect', (reason) => {
            setSocketConnected(false);
            // Server force-disconnected us — manually reconnect
            if (reason === 'io server disconnect') socket.connect();
        });

        socket.on('reconnect', () => {
            setSocketConnected(true);
            // Rejoin active room after reconnect
            const currentId = activeInteractionIdRef.current;
            if (currentId) socket.emit('join_interaction', currentId);
            // Re-fetch to catch missed events
            fetchDealDeskData(activeInteractionIdRef.current);
            fetchMyItems();
        });

        socket.on('reconnect_attempt', () => setSocketConnected(false));

        socket.on('connect_error', (err) => {
            setSocketConnected(false);
            console.warn('[Socket] connect error, polling active as fallback:', err.message);
        });

       socket.on('deal_desk_update', ({ interactionId }) => {
    if (interactionId === activeInteractionIdRef.current) {
        // Pass true here so a socket message arrival doesn't lock up the active viewport
        fetchDealDeskData(activeInteractionIdRef.current, true);
    }
    fetchMyItems();
});

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally empty — single persistent connection

    // ── Join/leave room when active interaction changes ───────────────────────
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket || !activeInteractionId) return;
        socket.emit('join_interaction', activeInteractionId);
        return () => {
            if (socketRef.current) socketRef.current.emit('leave_interaction', activeInteractionId);
        };
    }, [activeInteractionId]);

    // ── FIX: optimistic message expiry ────────────────────────────────────────
    const scheduleOptimisticExpiry = useCallback((tempId) => {
        const timer = setTimeout(() => {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            optimisticTimersRef.current.delete(tempId);
        }, OPTIMISTIC_EXPIRY_MS);
        optimisticTimersRef.current.set(tempId, timer);
    }, []);

    const cancelOptimisticExpiry = useCallback((tempId) => {
        const timer = optimisticTimersRef.current.get(tempId);
        if (timer) { clearTimeout(timer); optimisticTimersRef.current.delete(tempId); }
    }, []);

    useEffect(() => {
        return () => { optimisticTimersRef.current.forEach(t => clearTimeout(t)); };
    }, []);

    // ── Switch company ────────────────────────────────────────────────────────
//     const handleSelectCompany = useCallback(async (interactionId) => {
//     try {
//         // ⭐ ADD THIS LINE - Show loading when switching
//         setSwitchingConversation(true);
        
//         setCompanies(prev => prev.map(c =>
//             c.interaction_id === interactionId ? { ...c, unread_messages: 0 } : c
//         ));
//         setMyItems(prev => prev.map(item =>
//             (String(item.id) === String(loadOrTruckId) && item.type === type)
//                 ? { ...item, unreadCount: 0 } : item
//         ));
//         setUnreadScrollCount(0);
//         setIsAtBottom(true);
//         await api.put(`/api/deal-desk/messages/${interactionId}/read`);
//         setActiveInteractionId(interactionId);
//         activeInteractionIdRef.current = interactionId;
//         fetchDealDeskData(interactionId);
//         setTimeout(() => scrollToBottom('auto'), 50);
//     } catch (err) { 
//         console.error('Error switching company:', err); 
//     } finally {
//         // ⭐ ADD THIS LINE - Hide loading after done
//         setSwitchingConversation(false);
//     }
// }, [fetchDealDeskData, loadOrTruckId, type, scrollToBottom]);
// ── Switch company (Awaiting data fetch completely) ─────────────────────────
const handleSelectCompany = useCallback(async (interactionId) => {
    try {
        setSwitchingConversation(true);
        
        setCompanies(prev => prev.map(c =>
            c.interaction_id === interactionId ? { ...c, unread_messages: 0 } : c
        ));
        setMyItems(prev => prev.map(item =>
            (String(item.id) === String(loadOrTruckId) && item.type === type)
                ? { ...item, unreadCount: 0 } : item
        ));
        setUnreadScrollCount(0);
        setIsAtBottom(true);
        
        await api.put(`/api/deal-desk/messages/${interactionId}/read`);
        setActiveInteractionId(interactionId);
        activeInteractionIdRef.current = interactionId;
        
        // ⭐ CRITICAL: Await this call so the loading overlay stays up until data is back
        await fetchDealDeskData(interactionId);
        
        setTimeout(() => scrollToBottom('auto'), 50);
    } catch (err) { 
        console.error('Error switching company:', err); 
        setSwitchingConversation(false);
    }
}, [fetchDealDeskData, loadOrTruckId, type, scrollToBottom]);

    const handleSkipConversation = async (interactionId) => {
        const idToSkip = interactionId || activeInteractionId;
        if (!idToSkip) return;
        try {
            await api.post(`/api/deal-desk/interactions/${idToSkip}/skip`);
            const wasSkipped = companies.find(c => c.interaction_id === idToSkip)?.is_skipped;
            setSnackbar({ open: true, message: wasSkipped ? 'Conversation re-enabled.' : 'You have skipped this conversation.', severity: 'info' });
            fetchDealDeskData(activeInteractionId);
        } catch {
            setSnackbar({ open: true, message: 'Failed to skip conversation', severity: 'error' });
        }
    };

    const handleOfferAmountChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        const parts = val.split('.');
        if (parts[0].length > 10) parts[0] = parts[0].slice(0, 10);
        setOfferAmount(parts.join('.'));
    };

    const handleSendOffer = async () => {
        const amount = parseFloat(offerAmount);
        // FIX: granular negative/zero/empty error messages
        if (!offerAmount || isNaN(amount)) {
            setSnackbar({ open: true, message: 'Please enter a valid offer amount.', severity: 'error' });
            return;
        }
        if (amount < 0) {
            setSnackbar({ open: true, message: 'Negative rates are not allowed.', severity: 'error' });
            return;
        }
        if (amount === 0) {
            setSnackbar({ open: true, message: 'Offer amount must be greater than $0.', severity: 'error' });
            return;
        }
        if (!activeInteractionId) { setSnackbar({ open: true, message: 'Please select a conversation first.', severity: 'error' }); return; }
        if (interactionData?.is_skipped) { setSnackbar({ open: true, message: 'Conversation is no longer available.', severity: 'warning' }); return; }
        if (isSessionExpired || !interactionData) return;

        const tempOffer = {
            id: `temp-offer-${Date.now()}`, _kind: 'offer', _ts: Date.now(),
            _isOptimistic: true, offer_amount: amount, rate_per_mile: null,
            status: 'pending', offered_by_user_id: userData?.id,
            offered_by_company_name: userData?.companyName || 'You',
            offered_to_user_id: null, created_at: new Date().toISOString(),
        };
        setOffers(prev => [...prev, tempOffer]);
        setOfferAmount('');
        setIsAtBottom(true);
        setTimeout(() => scrollToBottom('smooth'), 30);

        setIsSending(true);
        try {
            await api.post('/api/deal-desk/offers', {
                interactionId: activeInteractionId, loadOrTruckId, type,
                offerAmount: amount, ratePerMile: null,
            });
            setSnackbar({ open: true, message: 'Offer sent successfully', severity: 'success' });
            fetchDealDeskData(activeInteractionId);
        } catch (error) {
            setOffers(prev => prev.filter(o => o.id !== tempOffer.id));
            setOfferAmount(String(amount));
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to send offer', severity: 'error' });
        } finally { setIsSending(false); }
    };

    const handleAcceptOffer = async (offerId) => {
        try {
            await api.put(`/api/deal-desk/offers/${offerId}/accept`);
            setSnackbar({ open: true, message: 'Offer accepted!', severity: 'success' });
            fetchDealDeskData(activeInteractionId);
        } catch (e) { setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to accept offer', severity: 'error' }); }
    };

    const handleRejectOffer = async (offerId) => {
        try {
            await api.put(`/api/deal-desk/offers/${offerId}/reject`);
            setSnackbar({ open: true, message: 'Offer rejected', severity: 'info' });
            fetchDealDeskData(activeInteractionId);
        } catch (e) { setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to reject offer', severity: 'error' }); }
    };

    const handleMessageAction = async (action, msg) => {
        setMsgMenuAnchor({ anchor: null, msg: null });
        if (action === 'copy') {
            navigator.clipboard.writeText(msg.message);
            setSnackbar({ open: true, message: 'Copied to clipboard' });
        } else if (action === 'delete') {
            try {
                await api.delete(`/api/deal-desk/messages/${msg.id}`);
                fetchDealDeskData(activeInteractionId);
            } catch { setSnackbar({ open: true, message: 'Delete failed', severity: 'error' }); }
        } else if (action === 'edit') {
    const diffMs = Date.now() - new Date(msg.created_at).getTime();
    if (diffMs > 300000) {
        setSnackbar({ open: true, message: 'Edit window (5 mins) has expired', severity: 'error' });
        return;
    }
    // ── Save original so we can rollback if API fails ──
    setMessages(prev => prev.map(m =>
        m.id === msg.id ? { ...m, _originalMessage: m.message } : m
    ));
    setMessageText(msg.message);
    setEditMessageId(msg.id);
}
    };

    // FIX: separate negative guard for counter offer
    const handleCounter = (offerAmt) => {
        const amt       = parseFloat(offerAmt);
        const isOwner   = userData?.id === interactionData?.posted_by_id;
        const suggested = isOwner ? amt + 100 : amt - 100;
        if (suggested < 0) {
            setSnackbar({ open: true, message: 'Negative rates are not allowed. Counter offer must be greater than $0.', severity: 'error' });
            return;
        }
        if (suggested === 0) {
            setSnackbar({ open: true, message: 'Counter offer must be greater than $0.', severity: 'error' });
            return;
        }
        setOfferAmount(String(suggested));
    };

    // ── Send / Edit message ───────────────────────────────────────────────────
    const handleSendMessage = async () => {
        if (!messageText.trim() || !activeInteractionId) return;

        const trimmed     = messageText.trim();
        const isEdit      = Boolean(editMessageId);
        const currentEditId = editMessageId; // capture before clearing

        if (!isEdit) {
            const tempId  = `temp-${Date.now()}`;
            const tempMsg = {
                id: tempId, _kind: 'message', _ts: Date.now(),
                _isOptimistic: true, message: trimmed,
                sender_user_id: userData?.id,
                sender_company_name: userData?.companyName || 'You',
                sender_role: userData?.role || '',
                is_deleted: false, is_edited: false,
                created_at: new Date().toISOString(),
            };
            setMessages(prev => [...prev, tempMsg]);
            setMessageText('');
            setIsAtBottom(true);
            setTimeout(() => scrollToBottom('smooth'), 30);
            scheduleOptimisticExpiry(tempId);

            setIsSending(true);
            try {
                await api.post('/api/deal-desk/messages', {
                    interactionId: activeInteractionId, loadOrTruckId, type, message: trimmed,
                });
                cancelOptimisticExpiry(tempId);
                fetchDealDeskData(activeInteractionId);
            } catch {
                cancelOptimisticExpiry(tempId);
                setMessages(prev => prev.filter(m => m.id !== tempId));
                setMessageText(trimmed);
                setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
            } finally { setIsSending(false); }

        } else {
    // ── OPTIMISTIC: update message text in UI instantly ──────────────
    setMessages(prev => prev.map(m =>
        m.id === currentEditId
            ? { ...m, message: trimmed, is_edited: true, _isPendingEdit: true }
            : m
    ));
    setMessageText('');
    setEditMessageId(null);

    setIsSending(true);
    try {
        await api.put(`/api/deal-desk/messages/${currentEditId}`, { message: trimmed });
        // Confirm: remove pending flag
        setMessages(prev => prev.map(m =>
            m.id === currentEditId
                ? { ...m, _isPendingEdit: false }
                : m
        ));
        fetchDealDeskData(activeInteractionId); // sync with server in background
    } catch {
        // Rollback on failure
        setMessages(prev => prev.map(m =>
            m.id === currentEditId
                ? { ...m, message: m._originalMessage || m.message, is_edited: false, _isPendingEdit: false }
                : m
        ));
        setMessageText(trimmed);
        setEditMessageId(currentEditId);
        setSnackbar({ open: true, message: 'Failed to edit message', severity: 'error' });
    } finally { setIsSending(false); }
}
    };

    // FIX: cancel edit mode cleanly
    const handleCancelEdit = () => {
        setEditMessageId(null);
        setMessageText('');
    };

    const handleScrollToBottomFab = () => {
        setUnreadScrollCount(0);
        setIsAtBottom(true);
        scrollToBottom('smooth');
    };

    const handleDownloadPDF = async () => {
        const timeline         = document.getElementById('deal-desk-timeline');
        const originalOverflow = timeline.style.overflow;
        timeline.style.overflow = 'visible';
        const canvas = await html2canvas(timeline, { scale: 2, useCORS: true });
        timeline.style.overflow = originalOverflow;

        const imgData   = canvas.toDataURL('image/png');
        const pdf       = new jsPDF('p', 'mm', 'a4');
        const imgProps  = pdf.getImageProperties(imgData);
        const pdfWidth  = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        const currentItem = leftItems.find(i => String(i.id) === String(loadOrTruckId));

        try { pdf.addImage(SD_logo, 'PNG', 155, 8, 45, 10); } catch (e) {
            console.error('[PDF] Logo failed to load:', e);
        }

        pdf.setFontSize(16); pdf.setTextColor(17, 34, 77);
        pdf.text('DealDesk Conversation', 10, 15);
        pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
        pdf.text(`Generated: ${dayjs().format('MM/DD/YYYY hh:mm A')}`, 10, 22);
        const userRole = (userData?.role || localStorage.getItem('role') || 'User').toLowerCase();
        pdf.text(`Account Type: ${userRole.charAt(0).toUpperCase() + userRole.slice(1)}`, 10, 28);
        pdf.text(`Participant: ${talkingTo || 'Unknown'}`, 10, 34);
        if (currentItem) {
            pdf.text(`Item: ${currentItem.type === 'load' ? 'LD-' : 'TRK-'}${currentItem.id}`, 10, 40);
            pdf.text(`Route: ${currentItem.origin} -> ${currentItem.destination}`, 10, 46);
            pdf.text(`Equipment: ${currentItem.equipment} | ${currentItem.length}ft | ${currentItem.weight} lbs`, 10, 52);
            pdf.text(`Posted at: ${dayjs(currentItem.timePosted).format('MM/DD/YYYY hh:mm A')}`, 10, 58);
        }

        const pageHeight = pdf.internal.pageSize.getHeight();
        let heightLeft   = pdfHeight;
        let position     = 65;
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= (pageHeight - 65);
        while (heightLeft >= 0) {
            position = heightLeft - pdfHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
            heightLeft -= pageHeight;
        }
        pdf.save(`DealDesk_${loadOrTruckId}_${dayjs().format('YYYYMMDD')}.pdf`);
    };

    // ── Timelines ─────────────────────────────────────────────────────────────
    const chatTimeline = useMemo(() => [
        ...offers.map(o   => ({ ...o, _kind: 'offer',   _ts: new Date(o.created_at).getTime() })),
        ...messages.map(m => ({ ...m, _kind: 'message', _ts: new Date(m.created_at).getTime() })),
    ].sort((a, b) => a._ts - b._ts), [offers, messages]);

    const chatTimelineWithDates = useMemo(() => {
        const result = [];
        let lastDateKey = null;
        for (const item of chatTimeline) {
            const itemDay = dayjs(item._ts).startOf('day');
            const dateKey = itemDay.format('YYYY-MM-DD');
            if (dateKey !== lastDateKey) {
                lastDateKey = dateKey;
                const diffDays = dayjs().startOf('day').diff(itemDay, 'day');
                let label;
                if (diffDays === 0)      label = 'Today';
                else if (diffDays === 1) label = 'Yesterday';
                else if (diffDays < 7)   label = itemDay.format('dddd');
                else                     label = itemDay.format('MMM D, YYYY');
                result.push({ _kind: '_date_sep', _ts: item._ts - 1, id: `datesep-${dateKey}`, label });
            }
            result.push(item);
        }
        return result;
    }, [chatTimeline]);

    // ── Derived state ─────────────────────────────────────────────────────────
    const leftItems = useMemo(() => {
        if (myItems.length > 0) return myItems;
        const combined = [
            ...allLoads.map(l  => ({ ...l, type: 'load' })),
            ...allTrucks.map(t => ({ ...t, type: 'truck' })),
        ];
        return combined.length === 0 && loadData ? [{ ...loadData, type: type || 'load' }] : combined;
    }, [myItems, allLoads, allTrucks, loadData, type]);

    const talkingTo = useMemo(() => {
        if (!interactionData) return null;
        return interactionData.posted_by_id === userData?.id
            ? interactionData.interested_company_name
            : interactionData.posted_by_company_name;
    }, [interactionData, userData]);

    const isOwnerOfItem    = useMemo(() => interactionData?.posted_by_id === userData?.id, [interactionData, userData]);
    const isSessionExpired = useMemo(() => {
        if (!interactionData?.last_activity_at) return false;
        return dayjs().diff(dayjs(interactionData.last_activity_at), 'hour') >= 24;
    }, [interactionData]);
    const leftPanelLabel = useMemo(() => {
        const hasLoads  = leftItems.some(i => i.type === 'load');
        const hasTrucks = leftItems.some(i => i.type === 'truck');
        if (hasLoads && hasTrucks) return 'Loads & Trucks';
        return hasTrucks ? 'Trucks' : 'Loads';
    }, [leftItems]);

    const hasItem  = Boolean(loadOrTruckId);
    const hasConvo = hasItem && Boolean(activeInteractionId);
    const PANEL_HEIGHT = 'calc(100vh - 60px)';

    // if (loading) {
    //     return (
    //         <ThemeProvider theme={theme}>
    //             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(180deg,#fff7e6 0%,#ffffff 100%)' }}>
    //                 <CircularProgress sx={{ color: '#11224d' }} />
    //             </Box>
    //         </ThemeProvider>
    //     );
    // }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ position: 'fixed', inset: 0, background: '#f6f8fc', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>

                {/* ── Header ── */}
                <HeaderBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={onBack}
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                bgcolor: '#11224d',
                                border: '1px solid rgba(255,255,255,.18)',
                                px: 1.5,
                                height: 28,
                                minHeight: 28,
                                display: 'flex',
                                alignItems: 'center',
                                '&:hover': { bgcolor: '#13275a' }
                            }}>
                            Loadboard
                        </Button>
                        <Box sx={{ width: 2, height: 24, bgcolor: 'rgba(255,255,255,.2)', borderRadius: 1 }} />
                        <PermContactCalendarIcon sx={{ color: '#ffffff', fontSize: '1.3rem' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>DealDesk</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* FIX: socket status indicator */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.9 }}
                            title={socketConnected ? 'Live — real-time updates active' : 'Reconnecting… polling active as fallback'}>
                            {socketConnected
                                ? <WifiIcon sx={{ fontSize: '0.9rem', color: '#fff' }} />
                                : <WifiOffIcon sx={{ fontSize: '0.9rem', color: '#ffcccc' }} />}
                            <Typography variant="caption" sx={{ color: socketConnected ? '#fff' : '#ffcccc', fontSize: '0.62rem', fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
                                {socketConnected ? 'Live' : 'Reconnecting…'}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, letterSpacing: '0.06em', display: { xs: 'none', sm: 'block' } }}>
                            Close deals faster &nbsp;•&nbsp; No calls needed
                        </Typography>
                        <Button onClick={onLogout} size="small" variant="contained" startIcon={<LogoutIcon />}
                            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, height: 30, fontSize: '0.7rem' }}>
                            Logout
                        </Button>
                    </Box>
                </HeaderBox>

                {/* ── Three-column body ── */}
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                    {/* Column 1: Loads / Trucks */}
                    <ColumnPanel sx={{ width: `${columnWidths.loads}px`, minWidth: '200px', flexShrink: 0, height: PANEL_HEIGHT, borderRadius: 0 }}>
                        <PanelHeader>{leftPanelLabel}</PanelHeader>
                        <ScrollArea>
                            {leftItems.length === 0
                                ? <Typography variant="caption" sx={{ p: 2, display: 'block', color: '#9aa' }}>No items</Typography>
                                : leftItems.map((item, i) => {
                                    const isLoad       = item.type === 'load';
                                    const isSel        = String(item.id) === String(loadOrTruckId);
                                    const label        = `${isLoad ? 'Load LD-' : 'Truck TRK-'}${item.id}`;
                                    const origin       = item.origin      || 'Anywhere';
                                    const dest         = item.destination || 'Anywhere';
                                    const equip        = item.equipment   || item.truckType || '—';
                                    const rate         = item.offerRate   || item.totalRate || item.rate;
                                    const miles        = item.tripMiles;
                                    const dateStr      = item.timePosted ? dayjs(item.timePosted).format('MM/DD/YYYY') : '';
                                    const isActiveItem = String(item.id) === String(loadOrTruckId) && item.type === type;
                                    const showUnread   = !isActiveItem && item.unreadCount > 0;
                                    return (
                                        <LoadRow key={i} selected={isSel ? 1 : 0} onClick={() => {
                                            setActiveInteractionId(null);
                                            activeInteractionIdRef.current = null;
                                            setUnreadScrollCount(0);
                                            setIsAtBottom(true);
                                            if (isLoad) onSelectLoad?.(item); else onSelectTruck?.(item);
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#11224d', mb: 0.3 }}>{label}</Typography>
                                                {showUnread && (
                                                    <Box sx={{ minWidth: 18, height: 18, borderRadius: '50%', bgcolor: '#e53935', color: '#fff', fontSize: '0.65rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        {item.unreadCount}
                                                    </Box>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#333', mb: 0.2 }}>{origin} → {dest}</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>
                                                {equip}{item.length ? ` • ${item.length}ft` : ''}{item.weight ? ` • ${Number(item.weight).toLocaleString()}lbs` : ''}
                                            </Typography>
                                            {(miles || rate) && (
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#11224d' }}>
                                                    {miles ? `${miles} mi` : ''}{miles && rate ? ' • ' : ''}{rate ? `$${Number(rate).toLocaleString()}` : ''}
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontSize: '0.65rem', color: '#8b8989', mt: 0.1 }}>
                                                {dateStr}{item.companyName ? ` • ${item.companyName}` : ''}
                                            </Typography>
                                            {(item.isActive === false || item.is_active === false) && (
                                                <Typography sx={{ fontSize: '0.65rem', color: '#ed6c02', fontWeight: 500, textAlign: 'center', mt: 0.5, display: 'block' }}>
                                                    No longer available
                                                </Typography>
                                            )}
                                        </LoadRow>
                                    );
                                })
                            }
                        </ScrollArea>
                    </ColumnPanel>
                    <Resizer onMouseDown={startResizing('loads')} />

                    {/* Column 2: Companies */}
                   {/* Column 2: Companies */}
<ColumnPanel sx={{ width: `${columnWidths.companies}px`, minWidth: '200px', flexShrink: 0, height: PANEL_HEIGHT, borderRadius: 0 }}>
    <PanelHeader>Companies</PanelHeader>
    <ScrollArea>
        {!hasItem ? (
            <Typography variant="caption" sx={{ p: 2, display: 'block', color: '#9aa' }}>Select an item</Typography>
        ) : loading ? (
            /* ⭐ SHOW LOADER HERE INSTEAD OF BLANKING OUT THE WHOLE PAGE */
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 8, gap: 1 }}>
                <CircularProgress size={28} sx={{ color: '#11224d' }} />
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Loading companies…</Typography>
            </Box>
        ) : companies.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <PermContactCalendarIcon sx={{ color: '#c0c5d8', fontSize: '2rem', mb: 1 }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#9aa' }}>No interested parties yet</Typography>
            </Box>
        ) : (
            companies.map((co, i) => {
                const isSel  = activeInteractionId === co.interaction_id;
                const unread = isSel ? 0 : (co.unread_messages || 0);
                return (
                    <CompanyRow key={i} selected={isSel ? 1 : 0} onClick={() => handleSelectCompany(co.interaction_id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#11224d', fontSize: '0.7rem', fontWeight: 800, flexShrink: 0 }}>
                                {(co.company_name || '?').charAt(0)}
                            </Avatar>
                            <Box sx={{ overflow: 'hidden' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#11224d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {co.company_name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: '#888', textTransform: 'capitalize' }}>{co.company_role}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {unread > 0 && (
                                <Box sx={{ minWidth: 18, height: 18, borderRadius: '50%', bgcolor: '#e53935', color: '#fff', fontSize: '0.65rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.5 }}>
                                    {unread}
                                </Box>
                            )}
                            {isOwnerOfItem && (
                                <Button size="small"
                                    onClick={(e) => { e.stopPropagation(); handleSkipConversation(co.interaction_id); }}
                                    sx={{ minWidth: 50, height: 22, fontSize: '0.65rem', textTransform: 'none', borderRadius: '6px', border: '1px solid', borderColor: co.is_skipped ? '#1976d2' : '#bdbdbd', bgcolor: co.is_skipped ? '#1976d2' : '#bdbdbd', color: '#fff', '&:hover': { bgcolor: co.is_skipped ? '#1565c0' : '#9e9e9e' } }}>
                                    Skip
                                </Button>
                            )}
                        </Box>
                    </CompanyRow>
                );
            })
        )}
    </ScrollArea>
</ColumnPanel>
                    <Resizer onMouseDown={startResizing('companies')} />

                    {/* Column 3: Conversation */}
                    <ColumnPanel sx={{ flex: 1, height: PANEL_HEIGHT, backgroundColor: '#ffffff' }}>
                        <PanelHeader>{hasConvo ? `Conversation with ${talkingTo || '…'}` : 'Conversation'}</PanelHeader>

                        {!hasConvo && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
                                <PermContactCalendarIcon sx={{ fontSize: '3rem', color: '#c0c5d8' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#11224d' }}>
                                    {!hasItem ? 'Select an item to begin' : 'Ready to Negotiate'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.85rem', color: '#666', maxWidth: 280 }}>
                                    {!hasItem ? 'Choose a load or truck from the left panel.'
                                        : companies.length > 0 ? 'Select a company from the list to start or continue your conversation.'
                                        : 'Waiting for interested parties to contact you.'}
                                </Typography>
                            </Box>
                        )}

                        {hasConvo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '4px 8px', borderBottom: '1px solid #e8eaf0', background: '#f8f9fc', flexShrink: 0 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#11224d', mr: 0.5 }}>Offer</Typography>
                                <TextField size="small" type="number" placeholder="Amount"
                                    disabled={interactionData?.is_skipped}
                                    value={offerAmount} onChange={handleOfferAmountChange}
                                    inputProps={{ min: 1, max: 9999999999, step: 1 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>$</Typography></InputAdornment> }}
                                    sx={{ width: 110, '& .MuiOutlinedInput-root': { height: 31, fontSize: '0.8rem' } }}
                                />
                                <Button variant="contained" onClick={handleSendOffer}
                                    disabled={isSending || interactionData?.is_skipped}
                                    sx={{ bgcolor: '#ffb300', color: '#11224d', fontWeight: 800, boxShadow: '0 6px 18px rgba(255,179,0,0.45)', height: 31, px: 2, fontSize: '0.8rem', '&:hover': { bgcolor: '#e6a000' } }}>
                                    Send Offer
                                </Button>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button size="small" variant="outlined" onClick={handleDownloadPDF}
                                    sx={{ height: 31, fontSize: '0.65rem', borderColor: '#4dabf5', color: '#4dabf5' }}>
                                    Print Chat
                                </Button>
                            </Box>
                        )}

                       {hasConvo && (
    <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* ⭐ BALANCED INTERIOR LOADER FOR CHAT TIMELINE PANEL */}
        {(switchingConversation || loading) && (
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                backdropFilter: 'blur(2px)',
            }}>
                <CircularProgress sx={{ color: '#11224d', mb: 2 }} size={35} />
                <Typography sx={{ fontSize: '0.85rem', color: '#11224d', fontWeight: 600 }}>
                    Loading conversation...
                </Typography>
            </Box>
        )}
        
        <ScrollArea id="deal-desk-timeline" ref={scrollAreaRef} onScroll={handleScroll} sx={{ p: '12px 14px', flex: 1 }}>
            {chatTimeline.length === 0 && (
                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#aaa', mt: 4 }}>
                                            No messages or offers yet. Start the conversation!
                                        </Typography>
                                    )}

                                    {chatTimelineWithDates.map((item) => {
                                        if (item._kind === '_date_sep') {
                                            return (
                                                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5, px: 1 }}>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#e8eaf0' }} />
                                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#aab', letterSpacing: '0.06em', textTransform: 'uppercase', bgcolor: '#f6f8fc', px: 1, borderRadius: 1, flexShrink: 0 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#e8eaf0' }} />
                                                </Box>
                                            );
                                        }

                                        if (item._kind === 'system_tip') {
                                            return (
                                                <SystemStatusMsg key="edit-tip">
                                                    <Chip label={item.message} size="small" variant="outlined" sx={{ fontSize: '0.65rem', opacity: 0.8 }} />
                                                </SystemStatusMsg>
                                            );
                                        }

                                        if (item._kind === 'message') {
                                            if (item.is_system) {
                                                return (
                                                    <SystemStatusMsg key={`sys-${item.id}`}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, px: 1.5, py: 0.7, maxWidth: '85%' }}>
                                                            <EditIcon sx={{ fontSize: '0.8rem', color: '#f59e0b', flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: '0.7rem', color: '#78550a', fontWeight: 600 }}>{item.message}</Typography>
                                                            <Typography sx={{ fontSize: '0.62rem', color: '#aaa', ml: 1, flexShrink: 0 }}>
                                                                {dayjs.utc(item.created_at).local().format('hh:mm A')}
                                                            </Typography>
                                                        </Box>
                                                    </SystemStatusMsg>
                                                );
                                            }

                                            if (item.is_deleted) {
                                                return (
                                                    <MsgBubble key={`msg-${item.id}`} isown={String(item.sender_user_id === userData?.id)}>
                                                        <Box className="bubble" sx={{ fontStyle: 'italic', opacity: 0.7 }}>This message was deleted</Box>
                                                    </MsgBubble>
                                                );
                                            }

                                            const isOwn       = item.sender_user_id === userData?.id;
                                            const roleLabel   = item.sender_role ? item.sender_role.charAt(0).toUpperCase() + item.sender_role.slice(1) : '';
                                            const displayName = `${roleLabel}: ${item.sender_company_name || (isOwn ? 'You' : 'Them')}`;

                                            return (
                                                <MsgBubble key={`msg-${item.id}`} isown={String(isOwn)}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#888', mb: 0.5, textAlign: isOwn ? 'right' : 'left' }}>
                                                            {displayName}
                                                        </Typography>
                                                    </Box>
                                                    <Box className="bubble" sx={{ border: editMessageId === item.id ? '2px solid #ffb300' : 'none', flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.message}</Typography>
                                                        {!item._isOptimistic && (
                                                            <IconButton size="small" sx={{ p: 0.5, ml: 1, opacity: 0.5, color: isOwn ? 'white' : 'inherit', zIndex: 10 }}
                                                                onClick={(e) => { e.stopPropagation(); setMsgMenuAnchor({ anchor: e.currentTarget, msg: item }); }}>
                                                                <MoreVertIcon sx={{ fontSize: '0.9rem' }} />
                                                            </IconButton>
                                                        )}
                                                        {item._isOptimistic && (
                                                            <Typography component="span" sx={{ fontSize: '0.6rem', ml: 1, opacity: 0.5 }}>sending…</Typography>
                                                        )}
                                                        {item.is_edited && !item._isOptimistic && (
                                                            <Typography component="span" sx={{ fontSize: '0.6rem', ml: 1, opacity: 0.6 }}>(edited)</Typography>
                                                        )}
                                                        {item._isPendingEdit && (
    <Typography component="span" sx={{ fontSize: '0.6rem', ml: 1, opacity: 0.5 }}>saving…</Typography>
)}
                                                    </Box>
                                                    <Typography sx={{ fontSize: '0.62rem', color: '#aaa', mt: 0.5, width: '100%', textAlign: isOwn ? 'right' : 'left' }}>
                                                        {item._isOptimistic ? '' : dayjs.utc(item.created_at).local().format('hh:mm A')}
                                                    </Typography>
                                                </MsgBubble>
                                            );
                                        }

                                        // ── Offer bubble ──────────────────────────────────────────
                                        const isOwnOffer = item.offered_by_user_id === userData?.id;
                                        return (
                                            <OfferBubble key={`offer-${item.id}`} role={isOwnOffer ? 'own' : 'other'} status={item.status}>
                                                <OfferCard status={item.status}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
                                                            {isOwnOffer ? 'Your offer' : `Offer from ${item.offered_by_company_name}`}
                                                        </Typography>
                                                        <StatusChip status={item.status}>{item.status}</StatusChip>
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#11224d', mb: 0.5 }}>
                                                        {formatCurrency(item.offer_amount)}
                                                    </Typography>
                                                    {item.rate_per_mile && (
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#888', mb: 0.5 }}>
                                                            {formatCurrency(item.rate_per_mile)} / mile
                                                        </Typography>
                                                    )}
                                                    {item._isOptimistic && (
                                                        <Typography sx={{ fontSize: '0.65rem', color: '#aaa', mt: 0.5, fontStyle: 'italic' }}>Sending…</Typography>
                                                    )}
                                                    {item.status === 'pending' && !interactionData?.is_skipped && !item._isOptimistic && (
                                                        <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, flexWrap: 'wrap' }}>
                                                            {!isOwnOffer && (
                                                                <Button size="small" variant="contained" onClick={() => handleAcceptOffer(item.id)}
                                                                    sx={{ bgcolor: '#43a047', color: '#fff', fontWeight: 700, fontSize: '0.72rem', px: 1.5, py: 0.4, '&:hover': { bgcolor: '#388e3c' } }}>
                                                                    Accept
                                                                </Button>
                                                            )}
                                                            <Button size="small" variant="outlined"
                                                                startIcon={<SwapHorizIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                                onClick={() => handleCounter(item.offer_amount)}
                                                                sx={{ borderColor: '#ffb300', color: '#b07800', fontWeight: 700, fontSize: '0.72rem', px: 1.2, py: 0.4 }}>
                                                                Counter
                                                            </Button>
                                                            {!isOwnOffer && (
                                                                <Button size="small" variant="contained" onClick={() => handleRejectOffer(item.id)}
                                                                    sx={{ bgcolor: '#e53935', color: '#fff', fontWeight: 700, fontSize: '0.72rem', px: 1.5, py: 0.4, '&:hover': { bgcolor: '#c62828' } }}>
                                                                    Reject
                                                                </Button>
                                                            )}
                                                            {isOwnOffer && (
                                                                <Button size="small" variant="outlined" onClick={() => handleRejectOffer(item.id)}
                                                                    sx={{ borderColor: '#e53935', color: '#e53935', fontWeight: 700, fontSize: '0.72rem', px: 1.5, py: 0.4 }}>
                                                                    Withdraw
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    )}
                                                </OfferCard>
                                            </OfferBubble>
                                        );
                                    })}

                                    {interactionData?.is_skipped && (
                                        <SystemStatusMsg>
                                            <Chip color="info"
                                                label={interactionData.skipped_by_user_id === userData?.id ? 'You skipped this conversation.' : 'This conversation is no longer available.'}
                                                size="small" sx={{ fontSize: '0.7rem', borderRadius: '6px' }} />
                                        </SystemStatusMsg>
                                    )}
                                    {isSessionExpired && (
                                        <SystemStatusMsg>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                This session has been inactive for 24 hours. Inactive sessions will be automatically deleted after 72 hours.
                                            </Typography>
                                        </SystemStatusMsg>
                                    )}
                                    <div ref={messagesEndRef} />
                                </ScrollArea>

                                {!isAtBottom && (
                                    <Box onClick={handleScrollToBottomFab} sx={{ position: 'absolute', bottom: 12, right: 16, zIndex: 10, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                        {unreadScrollCount > 0 && (
                                            <Box sx={{ bgcolor: '#e53935', color: '#fff', borderRadius: '12px', px: 1, py: 0.2, fontSize: '0.65rem', fontWeight: 800, minWidth: 22, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                                                {unreadScrollCount} new
                                            </Box>
                                        )}
                                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#11224d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(17,34,77,0.35)', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.1)' } }}>
                                            <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem' }} />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ── Message input ── */}
                        {hasConvo && (
                            <Box sx={{ flexShrink: 0 }}>
                                {/* FIX: visible edit mode banner with Cancel button */}
                                {editMessageId && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.5, bgcolor: '#fff8e1', borderTop: '1px solid #ffe082' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#78550a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <EditIcon sx={{ fontSize: '0.8rem' }} /> Editing message
                                        </Typography>
                                        <Button size="small" onClick={handleCancelEdit}
                                            sx={{ fontSize: '0.65rem', color: '#c0392b', minWidth: 0, px: 1 }}>
                                            Cancel
                                        </Button>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, p: '8px 12px', borderTop: '1px solid #e8eaf0', background: '#ffffff' }}>
                                    <TextField fullWidth size="small"
                                        placeholder={editMessageId ? 'Edit your message…' : 'Type a message…'}
                                        disabled={interactionData?.is_skipped}
                                        value={messageText} onChange={e => setMessageText(e.target.value)}
                                        onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey && !interactionData?.is_skipped) { e.preventDefault(); handleSendMessage(); } }}
                                        multiline maxRows={2}
                                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', background: '#ffffff', borderRadius: '10px' } }}
                                    />
                                    <Button variant="contained" onClick={handleSendMessage}
                                        disabled={isSending || interactionData?.is_skipped}
                                        sx={{ bgcolor: '#ffb300', color: '#11224d', minWidth: 44, px: 0, borderRadius: '10px', boxShadow: '0 8px 20px rgba(255,179,0,0.35)', '&:hover': { bgcolor: '#e6a000' } }}>
                                        <SendIcon sx={{ fontSize: '1.1rem' }} />
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </ColumnPanel>
                </Box>
            </Box>

            <Menu anchorEl={msgMenuAnchor.anchor} open={Boolean(msgMenuAnchor.anchor)}
                onClose={() => setMsgMenuAnchor({ anchor: null, msg: null })} sx={{ zIndex: 2100 }}>
                <MenuItem onClick={() => handleMessageAction('copy', msgMenuAnchor.msg)}>
                    <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>Copy
                </MenuItem>
                {msgMenuAnchor.msg?.sender_user_id === userData?.id && !msgMenuAnchor.msg?.is_deleted &&
                 dayjs.utc().diff(dayjs.utc(msgMenuAnchor.msg?.created_at), 'minute') < 5 && (
                    <MenuItem onClick={() => handleMessageAction('edit', msgMenuAnchor.msg)}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Edit
                    </MenuItem>
                )}
                {msgMenuAnchor.msg?.sender_user_id === userData?.id && (
                    <MenuItem onClick={() => handleMessageAction('delete', msgMenuAnchor.msg)} sx={{ color: 'error.main' }}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
                    </MenuItem>
                )}
            </Menu>

            <Snackbar open={snackbar.open} autoHideDuration={5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default DealDesk;